import { z } from 'zod';
import { AgentChatModelSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';
import {
  promptDetailsSchema,
  promptKindSchema,
  promptListItemSchema,
  promptMessageSchema,
} from '../types/prompts.js';

export const getPromptsResponseSchema = z.object({
  items: z.array(promptListItemSchema),
});

export const createPromptRequestBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().default(''),
  kind: promptKindSchema.default('default'),
  messages: z.array(promptMessageSchema).default([]),
  inputSchema: z.record(z.string(), z.unknown()).default({}),
  lastNMessagePairsToInclude: z.number().int().nonnegative().nullable().default(null),
  llmModel: AgentChatModelSchema.default('gpt-4.1-mini'),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
  publishOnCreate: z.boolean().default(false),
});

export const createPromptResponseSchema = z.object({
  id: z.number(),
  identifier: z.uuid(),
});

export const getPromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

export const getPromptResponseSchema = promptDetailsSchema;

export const updatePromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

export const updatePromptRequestBodySchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  kind: promptKindSchema,
  status: StatusTypeSchema,
  messages: z.array(promptMessageSchema),
  inputSchema: z.record(z.string(), z.unknown()),
  lastNMessagePairsToInclude: z.number().int().nonnegative().nullable().default(null),
  llmModel: AgentChatModelSchema,
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const deletePromptRequestParamsSchema = z.object({
  promptId: z.coerce.number(),
});

// --- Prompt Versions ---

export const promptVersionListItemSchema = z.object({
  id: z.number(),
  versionIdentifier: z.uuid(),
  version: z.number(),
  name: z.string(),
  description: z.string(),
  kind: promptKindSchema,
  lastNMessagePairsToInclude: z.number().int().nonnegative().nullable().optional(),
  llmModel: AgentChatModelSchema,
  messages: z.array(promptMessageSchema),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
  importedOn: z.coerce.date().nullable().optional(),
  importedBy: z.uuid().nullable().optional(),
  status: StatusTypeSchema,
  savedOn: z.coerce.date(),
});
export type PromptVersionListItem = z.infer<typeof promptVersionListItemSchema>;

export const getPromptVersionsResponseSchema = z.object({
  promptIdentifier: z.uuid(),
  promptCreatedOn: z.coerce.date(),
  versions: z.array(promptVersionListItemSchema),
});
export type GetPromptVersionsResponse = z.infer<typeof getPromptVersionsResponseSchema>;

export const promptCsvImportRowSchema = z.object({
  promptIdentifier: z.uuid(),
  promptVersionIdentifier: z.uuid(),
  name: z.string().trim().min(1),
  description: z.string().default(''),
  kind: promptKindSchema,
  status: StatusTypeSchema,
  llmModel: AgentChatModelSchema,
  lastNMessagePairsToInclude: z.number().int().nonnegative().nullable().default(null),
  messages: z.array(promptMessageSchema).default([]),
  inputSchema: z.record(z.string(), z.unknown()).default({}),
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().default(null),
});
export type PromptCsvImportRow = z.infer<typeof promptCsvImportRowSchema>;

export const importPromptsCsvRequestSchema = z.object({
  rows: z.array(promptCsvImportRowSchema).min(1),
});
export type ImportPromptsCsvRequest = z.infer<typeof importPromptsCsvRequestSchema>;

export const parsePromptsCsvResponseSchema = z.object({
  summary: z.object({
    totalRows: z.number().int().nonnegative(),
    uniquePromptIdentifiers: z.number().int().nonnegative(),
  }),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  isValid: z.boolean(),
});
export type ParsePromptsCsvResponse = z.infer<typeof parsePromptsCsvResponseSchema>;

export const importPromptsCsvWarningSchema = z.object({
  promptIdentifier: z.uuid(),
  message: z.string(),
});
export type ImportPromptsCsvWarning = z.infer<typeof importPromptsCsvWarningSchema>;

export const importPromptsCsvResponseSchema = z.object({
  summary: z.object({
    totalRows: z.number().int().nonnegative(),
    createdPromptCount: z.number().int().nonnegative(),
    updatedPromptCount: z.number().int().nonnegative(),
    createdVersionCount: z.number().int().nonnegative(),
    updatedVersionCount: z.number().int().nonnegative(),
  }),
  warnings: z.array(importPromptsCsvWarningSchema),
});
export type ImportPromptsCsvResponse = z.infer<typeof importPromptsCsvResponseSchema>;

export const recordQuerySubmissionRequestParamsSchema = z.object({
  promptIdentifier: z.uuid(),
});

export const recordQuerySubmissionRequestBodySchema = z.object({
  referenceId: z.string().optional(),
});

export const recordQuerySubmissionResponseSchema = z.object({
  success: z.literal(true),
});
export namespace UpdatePromptStatus {
  export const paramsSchema = z.object({
    promptId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    status: StatusTypeSchema,
  });
  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = promptDetailsSchema;
  export type Response = z.infer<typeof responseSchema>;
}

// --- Query Log ---

export const queryLogListItemSchema = z.object({
  queryIdentifier: z.string(),
  queryText: z.string(),
  promptId: z.number().nullable(),
  promptName: z.string().nullable(),
  queryType: z.enum(['instruction', 'user']),
  userEmail: z.string().nullable(),
  appIdentifier: z.string().nullable(),
  appName: z.string().nullable(),
  brandIdentifier: z.string().nullable(),
  brandName: z.string().nullable(),
  status: z.enum(['success', 'error']),
  tokenCount: z.number().nullish(),
  inputTokenCount: z.number().nullish(),
  outputTokenCount: z.number().nullish(),
  durationMs: z.number().nullish(),
  createdOn: z.coerce.date(),
});
export type QueryLogListItem = z.infer<typeof queryLogListItemSchema>;

export const queryLogDetailSchema = queryLogListItemSchema.extend({
  model: z.string().nullable(),
  langfuseTraceUrl: z.string().nullable(),
  promptMessages: z.array(promptMessageSchema).nullable(),
  response: z.unknown().nullable(),
});
export type QueryLogDetail = z.infer<typeof queryLogDetailSchema>;

export const queryLogStatsSchema = z.object({
  total: z.number(),
  successCount: z.number(),
  instructionCount: z.number(),
  userCount: z.number(),
  avgResponseDuration: z.number().nullable(),
});
export type QueryLogStats = z.infer<typeof queryLogStatsSchema>;

export const getQueryLogRequestQuerySchema = z.object({
  promptId: z.coerce.number().optional(),
  userEmail: z.string().optional(),
  appIdentifier: z.string().optional(),
  brandIdentifier: z.string().optional(),
  status: z.enum(['success', 'error']).optional(),
  search: z.string().optional(),
  take: z.coerce.number().int().min(0).max(500).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});
export type GetQueryLogRequestQuery = z.infer<typeof getQueryLogRequestQuerySchema>;

export const getQueryLogResponseSchema = z.object({
  items: z.array(queryLogListItemSchema),
  total: z.number(),
  stats: queryLogStatsSchema,
});

export const getQueryLogDetailResponseSchema = z.object({
  item: queryLogDetailSchema,
});
